import React, { useState } from "react";
import Reveal from "@/components/Reveal";
import { Play } from "lucide-react";

const MEDIA = {
  m1: "https://images.unsplash.com/photo-1761925098120-c33304cfa418?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxzdGFnZSUyMHBlcmZvcm1hbmNlJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzc5MzkyNTMyfDA&ixlib=rb-4.1.0&q=85",
  m2: "https://images.unsplash.com/photo-1774988353278-b01caf489c4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzdGFnZSUyMHBlcmZvcm1hbmNlJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzc5MzkyNTMyfDA&ixlib=rb-4.1.0&q=85",
  m3: "https://images.unsplash.com/photo-1766844649143-af98d71e346b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHRoZWF0ZXIlMjBhdWRpZW5jZSUyMGRhcmt8ZW58MHx8fHwxNzc5MzkyNTMyfDA&ixlib=rb-4.1.0&q=85",
  m4: "https://images.unsplash.com/photo-1518998053901-5348d3961a04?auto=format&fit=crop&w=1400&q=80",
  m5: "https://images.unsplash.com/photo-1542219550-37153d387c27?auto=format&fit=crop&w=1400&q=80",
};

function Tile({ src, label, span, ratio = "aspect-[4/5]", playable, testid, embedSrc }) {
  return (
    <div
      data-testid={testid}
      className={`relative img-hover group ${span} ${ratio} overflow-hidden border border-[#C5A059]/15`}
    >
      {embedSrc ? (
        <iframe
          src={embedSrc}
          title={label}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      ) : (
        <>
          <img
            src={src}
            alt={label}
            className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />

          {playable && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="w-16 h-16 rounded-full border border-[#C5A059] bg-[#050505]/60 backdrop-blur flex items-center justify-center group-hover:bg-[#C5A059] transition-all duration-500">
                <Play className="w-5 h-5 text-[#C5A059] group-hover:text-black ml-0.5" />
              </span>
            </div>
          )}
        </>
      )}

      {!embedSrc && (
        <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
          <span className="font-display text-[#F5F5F0] text-lg leading-tight">
            {label}
          </span>
          <span className="overline text-[10px]">{playable ? "Watch" : "View"}</span>
        </div>
      )}

      {embedSrc && (
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between pointer-events-none">
          <span className="font-display text-[#F5F5F0] text-xs md:text-sm leading-tight px-2 py-1 bg-[#050505]/70 backdrop-blur-sm border border-[#C5A059]/20">
            {label}
          </span>
        </div>
      )}
    </div>
  );
}

export default function Media() {
  const [filter, setFilter] = useState("all");
  const filters = [
    { id: "all", label: "All" },
    { id: "stage", label: "Stage" },
    { id: "close", label: "Close-up" },
    { id: "press", label: "Press" },
  ];

  return (
    <section
      id="media"
      data-testid="media-section"
      className="relative pt-7 md:pt-10 pb-28 md:pb-36 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="overline">02 — Media</span>
              <span className="gold-rule" />
            </div>
            <Reveal>
              <h2
                data-testid="media-headline"
                className="font-display text-[#F5F5F0] text-4xl sm:text-5xl md:text-6xl leading-[1.05] max-w-2xl"
              >
                The work, in
                <span className="font-italic-display text-[#C5A059]"> light </span>
                and shadow.
              </h2>
            </Reveal>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2" data-testid="media-filters">
            {filters.map((f) => (
              <button
                key={f.id}
                data-testid={`media-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 text-[0.7rem] uppercase tracking-[0.22em] rounded-full border transition-all duration-300 ${
                  filter === f.id
                    ? "border-[#C5A059] text-[#050505] bg-[#C5A059]"
                    : "border-[#C5A059]/25 text-[#F5F5F0]/70 hover:border-[#C5A059] hover:text-[#E5C07B]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bento grid (or Press video when Press filter active) */}
        <Reveal>
          {filter === "press" ? (
            <div data-testid="media-press" className="space-y-4">
              <div className="relative w-full aspect-video border border-[#C5A059]/20 overflow-hidden bg-black">
                <iframe
                  data-testid="press-video"
                  src="https://www.youtube.com/embed/rlyOunS-kC0?rel=0"
                  title="Press feature — Christopher DePamphilis"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="overline text-[10px]">Press Feature</span>
                <a
                  href="https://www.youtube.com/watch?v=rlyOunS-kC0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="overline text-[10px] hover:text-[#E5C07B] transition-colors"
                >
                  Watch on YouTube →
                </a>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
              {[
                {
                  testid: "media-tile-1",
                  embedSrc: "https://www.youtube.com/embed/4GMVSaxLb-I?rel=0",
                  label: "The Devil You Don't — Live Act",
                  span: "md:col-span-8",
                  ratio: "aspect-[16/10]",
                  categories: ["all", "close"],
                },
                {
                  testid: "media-tile-2",
                  embedSrc: "https://www.youtube.com/embed/rlyOunS-kC0?rel=0",
                  label: "The Devil You Don't — Interview",
                  span: "md:col-span-4",
                  ratio: "aspect-[16/10]",
                  categories: ["all", "press"],
                },
                {
                  testid: "media-tile-3",
                  embedSrc: "https://www.youtube.com/embed/Ger2FmtpNZs?rel=0",
                  label: "Live Stage Performance — Poe's Magic",
                  span: "md:col-span-4",
                  ratio: "aspect-square",
                  categories: ["all", "stage"],
                },
                {
                  testid: "media-tile-4",
                  embedSrc: "https://www.youtube.com/embed/RsuEpFQT3Cs?rel=0",
                  label: "Close-Up — Example",
                  span: "md:col-span-4",
                  ratio: "aspect-square",
                  categories: ["all", "close"],
                },
                {
                  testid: "media-tile-5",
                  embedSrc: "https://www.youtube.com/embed/PZ1RTwoms3Y?rel=0",
                  label: "Uncut Stage Example — French Drop, Japan",
                  span: "md:col-span-4",
                  ratio: "aspect-square",
                  categories: ["all", "stage"],
                },
              ]
                .filter((t) => t.categories.includes(filter))
                .map((t) => (
                  <Tile
                    key={t.testid}
                    testid={t.testid}
                    embedSrc={t.embedSrc}
                    src={t.src}
                    label={t.label}
                    span={t.span}
                    ratio={t.ratio}
                    playable={t.playable}
                  />
                ))}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}
